import { BybListItem, ListItemCell } from "@Components/game/common";
import PlaySessionTypes from "@Enums/PlaySessionTypes";
import useI18n from "@i18n";
import { Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { TFunction } from "i18next";
import { SessionTypeIcon } from "../helpers";
import ImprovementIndicator from "./components/improvement-indicator";

dayjs.extend(relativeTime);

interface SessionResultSmallProps {
  sessionType: string;
  gameMode: string;
  score: number;
  previousScore: number;
  updatedAt: Date;
  gameplayDuration: number;
  focuses?: string[];
  rating?: number;
  highlight?: boolean;
  Icon?: React.ReactNode;
  onClick?: () => void;
}

function formatDuration(totalSeconds: number, i18n: TFunction) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  // check if totalSeconds is less than 1 minute and return only seconds
  if (totalSeconds < 60) {
    return `${seconds}${i18n("seconds-extra-short")}`;
  }
  // check if totalSeconds is less than 1 hour and return minutes and seconds
  if (totalSeconds < 3600) {
    return `${minutes}${i18n("minutes-extra-short")} ${seconds}${i18n("seconds-extra-short")}`;
  }
  // return hours, minutes and seconds
  return `${Math.floor(totalSeconds / 3600)}${i18n("hours-short")} ${minutes.toFixed(0)}${i18n(
    "minutes-extra-short"
  )} ${seconds}${i18n("seconds-extra-short")}`;
}

const SessionResultSmall = ({
  sessionType,
  gameMode,
  score,
  previousScore,
  rating,
  updatedAt,
  gameplayDuration,
  highlight,
  Icon,
  onClick,
}: SessionResultSmallProps) => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

  const dateFormatCutoff: number = 1000 * 60 * 60 * 24 * 7;

  return (
    <BybListItem columns={isSmUp ? 6 : 4} onClick={onClick}>
      <ListItemCell first colSpan={3}>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          {!Icon ? <SessionTypeIcon sessionType={sessionType as PlaySessionTypes} gameMode={gameMode} size={isSmUp ? 9 : undefined} /> : Icon}
          <Stack>
            <Typography variant={isSmUp ? "text2xl700" : "textBase600"} color="dsBase.v200">
              {i18n(`session-type.${gameMode === "MATCH_PLAY" ? "matchplay_session" : sessionType}`)}
            </Typography>
            {!isSmUp && (
              <Stack direction="row" spacing={0}>
                <Typography variant="textS400" color={theme.palette.dsNeutral.v300}>
                  {(new Date().valueOf() - new Date(updatedAt).valueOf() > dateFormatCutoff
                    ? dayjs(new Date(updatedAt)).format("D MMM")
                    : dayjs(new Date(updatedAt)).fromNow()) +
                    " · " +
                    formatDuration(gameplayDuration || 0, i18n)}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </ListItemCell>
      {isSmUp ? (
        <>
          <ListItemCell>{rating}</ListItemCell>
          <ListItemCell>
            <ImprovementIndicator score={score} previousScore={previousScore} fontSize={theme.spacing(2.5)} highlightScore smallArrows />
          </ListItemCell>
          <ListItemCell>{score.toFixed(0)}</ListItemCell>
        </>
      ) : (
        <ListItemCell sx={{ alignItems: "end" }}>{score.toFixed(0)}</ListItemCell>
      )}
    </BybListItem>
  );
};

export default SessionResultSmall;
